/**
 * Front-end-only user UI preferences (persisted in localStorage).
 *
 * Shape in storage (key: USER_UI_PREFERENCES_STORAGE_KEY):
 *   { "<authSource>:<userId>": { patientDetailsHidden: true, ... }, ... }
 *
 * Scoped per signed-in user so two users on one browser keep separate settings.
 */

export const USER_UI_PREFERENCES_STORAGE_KEY = "medilis_ui_preferences";
export const USER_UI_PREFERENCES_CHANGE_EVENT = "medilis-ui-preferences-change";

/** Known preference keys. */
export const UI_PREFERENCE = {
  PATIENT_DETAILS_HIDDEN: "patientDetailsHidden",
  /** CRE AI Voice Editing hotkey: { code, ctrl, shift, alt, meta }. */
  AI_EDIT_HOTKEY: "aiEditHotkey",
};

/**
 * @param {string|null|undefined} authSource
 * @param {string|null|undefined} userId
 * @returns {string}
 */
export function buildUiPreferencesUserKey(authSource, userId) {
  const source = String(authSource || "unknown").toLowerCase();
  const id = String(userId || "").trim();
  return `${source}:${id || "anonymous"}`;
}

function readAll() {
  try {
    const raw = localStorage.getItem(USER_UI_PREFERENCES_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed
      : {};
  } catch (_) {
    return {};
  }
}

/**
 * @param {string} userKey
 * @param {string} prefKey
 * @param {*} defaultValue
 */
export function readUserUiPreference(userKey, prefKey, defaultValue) {
  const all = readAll();
  const prefs = all[userKey];
  if (prefs && typeof prefs === "object" && prefKey in prefs) {
    return prefs[prefKey];
  }
  return defaultValue;
}

/**
 * @param {string} userKey
 * @param {string} prefKey
 * @param {*} value
 * @returns {boolean} true if persisted
 */
export function writeUserUiPreference(userKey, prefKey, value) {
  try {
    const all = readAll();
    const prefs =
      all[userKey] && typeof all[userKey] === "object" ? all[userKey] : {};
    all[userKey] = { ...prefs, [prefKey]: value };
    localStorage.setItem(USER_UI_PREFERENCES_STORAGE_KEY, JSON.stringify(all));
  } catch (_) {
    return false;
  }
  try {
    window.dispatchEvent(
      new CustomEvent(USER_UI_PREFERENCES_CHANGE_EVENT, {
        detail: { userKey, prefKey, value },
      }),
    );
  } catch (_) {
    // ignore (non-browser env)
  }
  return true;
}
